import './HowDoesItWorkSection.scss';

import { SimCard } from '@phosphor-icons/react';
import { HowDoesItWork } from '@travelwin/cms/types';
import { ButtonV2, Icon, Title } from '@travelwin/core';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClientOnly } from 'remix-utils/client-only';

import { ImageMedia } from '@/components/ImageMedia';
import { RichText } from '@/components/RichText';
import { serializeLexicalChildNode } from '@/components/RichText/serialize-lexical';
import { YouTubeEmbedWrapper } from '@/components/YouTubeEmbedWrapper';
import { useDeviceOS } from '@/hooks/useDeviceOS';
import { useExternalHomepageConfig } from '@/hooks/useExternalHomepageConfig';

import CompatibleDevicePopOver from './CompatibleDevicePopOver/CompatibleDevicePopOver';

const className = 'c-HowDoesItWorkSection';

export const HowDoesItWorkSection = ({
  content,
}: {
  content: HowDoesItWork;
}) => {
  const [popoverOpen, setPopoverOpen] = useState(false);

  const { t } = useTranslation();
  const { getHomepageUrl } = useExternalHomepageConfig();

  const handleClose = () => {
    setPopoverOpen(false);
  };

  const deviceCompatibilityHasContent =
    content.deviceCompatibility && content.deviceCompatibility.length > 0;

  return (
    <div className={className}>
      <Title level={2} tag={2} className={`${className}__title`}>
        {serializeLexicalChildNode({ content: content.title })}
      </Title>
      {deviceCompatibilityHasContent && (
        <CompatibleDevicePopOver
          content={content.deviceCompatibility}
          open={popoverOpen}
          handleClose={handleClose}
        />
      )}
      <div className={`${className}__steps`}>
        <ImageStep
          index={1}
          title={serializeLexicalChildNode({ content: content.stepOne?.title })}
          body={<RichText content={content.stepOne?.body} />}
          image={
            <ImageMedia
              media={content.stepOne?.image}
              className={`${className}__image`}
              sizes="(min-width: 992px) 33vw, 160px"
            />
          }
          cta={
            deviceCompatibilityHasContent ? (
              <ButtonV2
                variant="primary"
                intent="neutral-gray"
                size="large"
                icons={{ leading: <SimCard size={30} /> }}
                onClick={() => setPopoverOpen(true)}
              >
                {t(
                  'howDoesItWork.checkCompatibility',
                  'Check Device Compatibility',
                )}
              </ButtonV2>
            ) : (
              <ButtonV2
                variant="primary"
                intent="neutral-gray"
                size="large"
                icons={{ leading: <SimCard size={20} /> }}
                asChild
              >
                <a
                  href={getHomepageUrl('/support/compatible-handsets')}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t(
                    'howDoesItWork.checkCompatibility',
                    'Check Device Compatibility',
                  )}
                </a>
              </ButtonV2>
            )
          }
        />
        <InstructionsStep
          index={2}
          title={serializeLexicalChildNode({ content: content.stepTwo?.title })}
          body={<RichText content={content.stepTwo?.body} />}
          iosVideoUrl={content.stepTwo?.iosVideoUrl}
          androidVideoUrl={content.stepTwo?.androidVideoUrl}
        />
        <ImageStep
          index={3}
          title={serializeLexicalChildNode({
            content: content.stepThree?.title,
          })}
          body={<RichText content={content.stepThree?.body} />}
          image={
            <ImageMedia
              media={content.stepThree?.image}
              className={`${className}__image`}
              sizes="(min-width: 992px) 33vw, 160px"
            />
          }
        />
      </div>
    </div>
  );
};

const Step = ({
  index,
  children,
  cta,
}: {
  index: number;
  children: ReactNode;
  cta?: ReactNode;
}) => {
  return (
    <div className={`${className}__step`}>
      <div className={`${className}__step-counter`}>{index}</div>
      {children}
      {cta && <div className={`${className}__step-cta`}>{cta}</div>}
    </div>
  );
};

const StepBody = ({ title, body }: { title: ReactNode; body: ReactNode }) => {
  return (
    <div className={`${className}__step-body-container`}>
      <Title level={5} tag={3} className={`${className}__step-title`}>
        {title}
      </Title>
      {body}
    </div>
  );
};

const ImageStep = ({
  index,
  title,
  body,
  image,
  cta,
}: {
  index: number;
  title: ReactNode;
  body: ReactNode;
  image: ReactNode;
  cta?: ReactNode;
}) => {
  return (
    <Step index={index} cta={cta}>
      <div className={`${className}__step-container-responsive`}>
        <div>{image}</div>
        <StepBody title={title} body={body} />
      </div>
    </Step>
  );
};

const InstructionsStep = ({
  index,
  title,
  body,
  cta,
  iosVideoUrl,
  androidVideoUrl,
}: {
  index: number;
  title: ReactNode;
  body: ReactNode;
  cta?: ReactNode;
  iosVideoUrl: string | undefined | null;
  androidVideoUrl: string | undefined | null;
}) => {
  const { t } = useTranslation();
  const { isMobileOS, isiOSOS, isAndroidOS } = useDeviceOS();
  const [video, setVideo] = useState<string | null>(null);

  return (
    <Step index={index} cta={cta}>
      <div className={`${className}__step-container`}>
        <div>
          <div className={`${className}__step-video-container`}>
            {video ? (
              <div className={`${className}__step-video-wrapper`}>
                <YouTubeEmbedWrapper
                  url={video}
                  youTubeProps={{ opts: { playerVars: { autoplay: 1 } } }}
                />
              </div>
            ) : (
              <ClientOnly>
                {() => (
                  <div className={`${className}__buttons-container`}>
                    {iosVideoUrl && (isiOSOS || !isMobileOS) && (
                      <ButtonV2
                        variant="primary"
                        intent="neutral-gray"
                        size="large"
                        icons={{ leading: <Icon name="appleLogo" size={20} /> }}
                        onClick={() => setVideo(iosVideoUrl)}
                      >
                        {t('howDoesItWork.playVideo', 'Play Video')}
                      </ButtonV2>
                    )}

                    {androidVideoUrl && (isAndroidOS || !isMobileOS) && (
                      <ButtonV2
                        variant="primary"
                        intent="neutral-gray"
                        size="large"
                        icons={{
                          leading: <Icon name="androidLogo" size={20} />,
                        }}
                        onClick={() => setVideo(androidVideoUrl)}
                      >
                        {t('howDoesItWork.playVideo', 'Play Video')}
                      </ButtonV2>
                    )}
                  </div>
                )}
              </ClientOnly>
            )}
          </div>
        </div>
        <StepBody title={title} body={body} />
      </div>
    </Step>
  );
};
